import {
  DATABRICKS_AUTHORING_CERTIFICATION_CHECKS,
  DATABRICKS_RELEASE_CERTIFICATION_CHECKS,
  type DatabricksCertificationEvidence,
} from "./certification.js";

export interface DatabricksAppRecoveryEvidence {
  schemaVersion: number;
  target: string;
  preparedAt?: string;
  verifiedAt?: string;
  restartRecovery?: {
    mode?: "explicit-name-stop-start";
    deploymentIdChanged?: boolean;
    sourceCodePathPreserved?: boolean;
    session?: boolean;
    submission?: boolean;
    conversationOffset?: boolean;
    attachment?: boolean;
  };
  approvalRecovery?: {
    identityPreserved?: boolean;
    resumedAfterRestart?: boolean;
    completedAfterApproval?: boolean;
  };
  cascadeDeletion?: {
    sessions?: number;
    submissions?: number;
    conversationStreams?: number;
    attachments?: number;
  };
}

export interface DatabricksAppUserIsolationEvidence {
  schemaVersion: number;
  target: string;
  generatedAt?: string;
  principalsDistinct?: boolean;
  ownerCanList?: boolean;
  ownerCanRead?: boolean;
  otherCannotList?: boolean;
  otherDirectReadStatus?: number;
  secretsAbsentFromResponses?: boolean;
  principalIdentifiersRetained?: boolean;
  dynamicAgentIsolation?: boolean;
  dynamicOwnerCanRead?: boolean;
  dynamicOtherConversationStatus?: number;
  dynamicOtherSubmissionStatus?: number;
  dynamicOtherSessionStatus?: number;
  dynamicOtherAdmissionStatus?: number;
  runIdHash?: string;
  dynamicInstanceIdHash?: string;
}

export interface DatabricksDynamicAgentEvidence {
  schemaVersion: number;
  target: string;
  preparedAt?: string;
  verifiedAt?: string;
  hookRendering?: boolean;
  conditionalModelAndTools?: boolean;
  durableToolStep?: boolean;
  crashRecoveryAfterCheckpoint?: boolean;
  exactlyOneToolStep?: boolean;
  persistentStateAfterRestart?: boolean;
  immutableInitialDataAcrossNamedSessions?: boolean;
  resourceChangeNarration?: boolean;
  dataParts?: boolean;
  responseMetadata?: boolean;
  lifecycleHooks?: boolean;
  externalDatabricksJob?: boolean;
  stableExternalIdempotency?: boolean;
  approvalRecovery?: boolean;
  rejectedApprovalPreventedExecution?: boolean;
  modelCancellation?: boolean;
  governedToolCancellation?: boolean;
  cancellationCleanup?: boolean;
  dynamicManagedMcp?: boolean;
  boundedSubagent?: boolean;
  scopedSubagentTool?: boolean;
  selfDispatchActorTenantPreserved?: boolean;
  responsesApi?: boolean;
  previousResponseId?: boolean;
  responsesStreaming?: boolean;
  cleanup?: boolean;
  instanceIdHash?: string;
  checkpointHash?: string;
}

export interface ValidateDatabricksReleaseEvidenceOptions {
  evidence: DatabricksCertificationEvidence;
  expectedPackageVersion: string;
  expectedCommitSha: string;
  expectedPackageArtifactSha256: string;
  expectedAppArtifactSha256: string;
  expectedRelatedEvidenceSha256?: Readonly<Record<string, string>>;
  maxAgeMs?: number;
  now?: Date;
}

export interface DatabricksReleaseEvidenceValidation {
  valid: true;
  evidenceId: string;
  packageVersion: string;
  requiredChecks: readonly string[];
}

export interface ValidateDatabricksCrossTierEvidenceOptions {
  consumption: DatabricksCertificationEvidence;
  authoring: DatabricksCertificationEvidence;
  expectedPackageVersion?: string;
  expectedCommitSha?: string;
  expectedPackageArtifactSha256?: string;
  maxAgeMs?: number;
  now?: Date;
}

export interface DatabricksCrossTierEvidenceManifest {
  schemaVersion: 1;
  valid: true;
  generatedAt: string;
  packageVersion: string;
  commitSha: string;
  packageArtifactSha256: string;
  consumption: { evidenceId: string; generatedAt: string };
  authoring: { evidenceId: string; generatedAt: string };
}

export interface ValidateDatabricksRollingEvidenceOptions {
  consumption: readonly DatabricksCertificationEvidence[];
  crossTier: readonly DatabricksCrossTierEvidenceManifest[];
  requiredDays?: number;
  now?: Date;
}

export interface DatabricksRollingEvidenceReport {
  schemaVersion: 1;
  valid: true;
  generatedAt: string;
  windowStart: string;
  windowEnd: string;
  requiredDays: number;
  consumptionDates: string[];
  crossTierDates: string[];
  packageVersions: string[];
}

/**
 * Fail closed unless Tier R and Tier A exercised the same immutable package tarball and commit.
 */
export function validateDatabricksCrossTierEvidence(
  options: ValidateDatabricksCrossTierEvidenceOptions,
): DatabricksCrossTierEvidenceManifest {
  const { consumption, authoring } = options;
  const errors: string[] = [];
  const now = options.now ?? new Date();

  validateTierEvidence(
    "consumption",
    consumption,
    DATABRICKS_RELEASE_CERTIFICATION_CHECKS,
    "R",
    errors,
  );
  validateTierEvidence(
    "authoring",
    authoring,
    DATABRICKS_AUTHORING_CERTIFICATION_CHECKS,
    "A",
    errors,
  );

  if (!consumption.packageVersion || consumption.packageVersion !== authoring.packageVersion) {
    errors.push("consumption and authoring packageVersion values must match");
  }
  if (!consumption.commitSha || consumption.commitSha !== authoring.commitSha) {
    errors.push("consumption and authoring commitSha values must match");
  }
  if (
    !consumption.packageArtifactSha256 ||
    consumption.packageArtifactSha256 !== authoring.packageArtifactSha256
  ) {
    errors.push("consumption and authoring package artifact digests must match");
  }
  if (
    options.expectedPackageVersion &&
    consumption.packageVersion !== options.expectedPackageVersion
  ) {
    errors.push(`packageVersion does not match expected ${options.expectedPackageVersion}`);
  }
  if (options.expectedCommitSha && consumption.commitSha !== options.expectedCommitSha) {
    errors.push(`commitSha does not match expected ${options.expectedCommitSha}`);
  }
  if (
    options.expectedPackageArtifactSha256 &&
    consumption.packageArtifactSha256 !== options.expectedPackageArtifactSha256
  ) {
    errors.push("package artifact digest does not match the supplied candidate");
  }

  if (options.maxAgeMs !== undefined) {
    for (const [tier, evidence] of [
      ["consumption", consumption],
      ["authoring", authoring],
    ] as const) {
      const generatedAt = Date.parse(evidence.generatedAt);
      if (!Number.isFinite(generatedAt)) errors.push(`${tier} generatedAt is invalid`);
      else if (now.getTime() - generatedAt > options.maxAgeMs)
        errors.push(`${tier} evidence is stale`);
      else if (generatedAt > now.getTime() + 5 * 60_000)
        errors.push(`${tier} generatedAt is in the future`);
    }
  }

  if (errors.length > 0) {
    throw new Error(
      `Databricks cross-tier evidence is invalid:\n- ${[...new Set(errors)].join("\n- ")}`,
    );
  }

  return {
    schemaVersion: 1,
    valid: true,
    generatedAt: now.toISOString(),
    packageVersion: requiredEvidenceValue(consumption.packageVersion, "packageVersion"),
    commitSha: requiredEvidenceValue(consumption.commitSha, "commitSha"),
    packageArtifactSha256: requiredEvidenceValue(
      consumption.packageArtifactSha256,
      "packageArtifactSha256",
    ),
    consumption: {
      evidenceId: consumption.evidenceId,
      generatedAt: consumption.generatedAt,
    },
    authoring: {
      evidenceId: authoring.evidenceId,
      generatedAt: authoring.generatedAt,
    },
  };
}

function validateTierEvidence(
  label: string,
  evidence: DatabricksCertificationEvidence,
  checks: readonly string[],
  tier: "R" | "A",
  errors: string[],
): void {
  if (evidence.schemaVersion !== 2) errors.push(`${label} schemaVersion must be 2`);
  if (!evidence.passed) errors.push(`${label} certification did not pass`);
  for (const checkId of checks) {
    if (!evidence.requiredChecks.includes(checkId)) {
      errors.push(`${label} Tier ${tier} check ${checkId} is absent from requiredChecks`);
      continue;
    }
    const result = evidence.results.find((candidate) => candidate.id === checkId);
    if (!result || !result.required || result.status !== "passed" || result.tier !== tier) {
      errors.push(`${label} Tier ${tier} check ${checkId} does not have a required passing result`);
    }
  }
}

/**
 * Require daily Tier R evidence plus at least two same-candidate Tier R/A proofs in the rolling
 * public-claim window. Every Tier R record must bind two-user App isolation.
 */
export function validateDatabricksRollingEvidence(
  options: ValidateDatabricksRollingEvidenceOptions,
): DatabricksRollingEvidenceReport {
  const now = options.now ?? new Date();
  const requiredDays = options.requiredDays ?? 14;
  if (!Number.isInteger(requiredDays) || requiredDays < 2) {
    throw new Error("Databricks rolling evidence requiredDays must be an integer of at least 2.");
  }
  const end = utcDay(now);
  const expectedDates = Array.from({ length: requiredDays }, (_, offset) =>
    utcDateKey(new Date(end.getTime() - (requiredDays - 1 - offset) * 86_400_000)),
  );
  const expectedDateSet = new Set(expectedDates);
  const errors: string[] = [];
  const consumptionByDate = new Map<string, DatabricksCertificationEvidence>();
  for (const evidence of options.consumption) {
    const generatedAt = Date.parse(evidence.generatedAt);
    if (!Number.isFinite(generatedAt)) {
      errors.push(`Tier R evidence ${evidence.evidenceId} has an invalid generatedAt`);
      continue;
    }
    const date = utcDateKey(new Date(generatedAt));
    if (!expectedDateSet.has(date)) continue;
    const tierErrors: string[] = [];
    validateTierEvidence(
      `Tier R evidence ${evidence.evidenceId}`,
      evidence,
      DATABRICKS_RELEASE_CERTIFICATION_CHECKS,
      "R",
      tierErrors,
    );
    if (!evidence.relatedEvidenceSha256?.["app-user-isolation"]?.match(/^[a-f0-9]{64}$/)) {
      tierErrors.push(`Tier R evidence ${evidence.evidenceId} is not bound to App user isolation`);
    }
    if (tierErrors.length > 0) {
      errors.push(...tierErrors);
      continue;
    }
    const previous = consumptionByDate.get(date);
    if (!previous || Date.parse(previous.generatedAt) < generatedAt) {
      consumptionByDate.set(date, evidence);
    }
  }
  for (const date of expectedDates) {
    if (!consumptionByDate.has(date)) errors.push(`Tier R evidence is missing for ${date}`);
  }

  const consumptionById = new Map(
    [...consumptionByDate.values()].map((evidence) => [evidence.evidenceId, evidence]),
  );
  const validCrossTier = options.crossTier.filter((manifest) => {
    const generatedAt = Date.parse(manifest.generatedAt);
    if (!Number.isFinite(generatedAt) || !expectedDateSet.has(utcDateKey(new Date(generatedAt)))) {
      return false;
    }
    const consumption = consumptionById.get(manifest.consumption.evidenceId);
    if (
      manifest.schemaVersion !== 1 ||
      manifest.valid !== true ||
      !consumption ||
      manifest.packageVersion !== consumption.packageVersion ||
      manifest.commitSha !== consumption.commitSha ||
      manifest.packageArtifactSha256 !== consumption.packageArtifactSha256
    ) {
      errors.push(`Cross-tier evidence for ${manifest.generatedAt} does not match retained Tier R`);
      return false;
    }
    return true;
  });
  const crossTierDates = [
    ...new Set(validCrossTier.map((item) => utcDateKey(new Date(item.generatedAt)))),
  ].sort();
  if (crossTierDates.length < 2) {
    errors.push("At least two distinct cross-tier certification dates are required in the window");
  }

  if (errors.length > 0) {
    throw new Error(
      `Databricks rolling evidence is invalid:\n- ${[...new Set(errors)].join("\n- ")}`,
    );
  }
  return {
    schemaVersion: 1,
    valid: true,
    generatedAt: now.toISOString(),
    windowStart: `${expectedDates[0]}T00:00:00.000Z`,
    windowEnd: `${expectedDates.at(-1)}T23:59:59.999Z`,
    requiredDays,
    consumptionDates: expectedDates,
    crossTierDates,
    packageVersions: [
      ...new Set(
        [...consumptionByDate.values()]
          .map((evidence) => evidence.packageVersion)
          .filter((value): value is string => Boolean(value)),
      ),
    ].sort(),
  };
}

function requiredEvidenceValue(value: string | undefined, field: string): string {
  if (!value) throw new Error(`Databricks evidence ${field} is missing.`);
  return value;
}

function utcDay(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
}

function utcDateKey(value: Date): string {
  return value.toISOString().slice(0, 10);
}

/**
 * Fail closed unless retained Databricks evidence proves the exact release commit and artifacts.
 */
export function validateDatabricksReleaseEvidence(
  options: ValidateDatabricksReleaseEvidenceOptions,
): DatabricksReleaseEvidenceValidation {
  const { evidence } = options;
  const errors: string[] = [];
  if (evidence.schemaVersion !== 2) errors.push("schemaVersion must be 2");
  if (!evidence.passed) errors.push("certification did not pass");
  if (evidence.packageVersion !== options.expectedPackageVersion) {
    errors.push(
      `packageVersion mismatch: expected ${options.expectedPackageVersion}, received ${evidence.packageVersion ?? "missing"}`,
    );
  }
  if (evidence.commitSha !== options.expectedCommitSha) {
    errors.push(
      `commitSha mismatch: expected ${options.expectedCommitSha}, received ${evidence.commitSha ?? "missing"}`,
    );
  }
  if (evidence.packageArtifactSha256 !== options.expectedPackageArtifactSha256) {
    errors.push("package artifact digest does not match the release candidate");
  }
  if (evidence.artifactSha256 !== options.expectedAppArtifactSha256) {
    errors.push("Databricks App artifact digest does not match the release candidate");
  }

  for (const checkId of DATABRICKS_RELEASE_CERTIFICATION_CHECKS) {
    if (!evidence.requiredChecks.includes(checkId)) {
      errors.push(`Tier R check ${checkId} is absent from requiredChecks`);
      continue;
    }
    const result = evidence.results.find((candidate) => candidate.id === checkId);
    if (!result) {
      errors.push(`Tier R check ${checkId} has no result`);
      continue;
    }
    if (!result.required) errors.push(`Tier R check ${checkId} was not marked required`);
    if (result.tier !== "R")
      errors.push(`Tier R check ${checkId} was recorded as ${result.tier ?? "untyped"}`);
    if (result.status !== "passed") {
      errors.push(`Tier R check ${checkId} was ${result.status}`);
    }
  }

  for (const checkId of evidence.requiredChecks) {
    const result = evidence.results.find((candidate) => candidate.id === checkId);
    if (!result || result.status !== "passed") {
      errors.push(`required check ${checkId} does not have a passing result`);
    }
  }

  for (const kind of ["app-recovery", "dynamic-agent"] as const) {
    if (!evidence.relatedEvidenceSha256?.[kind]?.match(/^[a-f0-9]{64}$/)) {
      errors.push(`related evidence ${kind} is missing or is not a SHA-256 digest`);
    }
  }

  for (const [kind, digest] of Object.entries(options.expectedRelatedEvidenceSha256 ?? {})) {
    if (evidence.relatedEvidenceSha256?.[kind] !== digest) {
      errors.push(`related evidence ${kind} is missing or has the wrong digest`);
    }
  }

  for (const [field, value] of [
    ["workspaceCloud", evidence.workspaceCloud],
    ["workspaceRegion", evidence.workspaceRegion],
    ["authMode", evidence.authMode],
    ["principalKind", evidence.principalKind],
    ["runtimeVersion", evidence.runtimeVersion],
  ] as const) {
    if (!value?.trim()) errors.push(`${field} is missing`);
  }

  if (options.maxAgeMs !== undefined) {
    const generatedAt = Date.parse(evidence.generatedAt);
    const now = (options.now ?? new Date()).getTime();
    if (!Number.isFinite(generatedAt)) errors.push("generatedAt is invalid");
    else if (now - generatedAt > options.maxAgeMs) errors.push("certification evidence is stale");
    else if (generatedAt > now + 5 * 60_000) errors.push("generatedAt is in the future");
  }

  if (errors.length > 0) {
    throw new Error(
      `Databricks release evidence is invalid:\n- ${[...new Set(errors)].join("\n- ")}`,
    );
  }
  return {
    valid: true,
    evidenceId: evidence.evidenceId,
    packageVersion: options.expectedPackageVersion,
    requiredChecks: [...DATABRICKS_RELEASE_CERTIFICATION_CHECKS],
  };
}

/** Assert that App restart evidence proves durable state, approval recovery, and cascade cleanup. */
export function validateDatabricksAppRecoveryEvidence(
  evidence: DatabricksAppRecoveryEvidence,
): void {
  const errors: string[] = [];
  if (evidence.schemaVersion !== 1) errors.push("schemaVersion must be 1");
  if (evidence.target !== "databricks-app") errors.push("target must be databricks-app");
  if (!evidence.preparedAt || !evidence.verifiedAt)
    errors.push("prepare and verify timestamps are required");
  if (evidence.restartRecovery?.mode !== "explicit-name-stop-start")
    errors.push("restartRecovery.mode must be explicit-name-stop-start");
  const restartFields = [
    "deploymentIdChanged",
    "sourceCodePathPreserved",
    "session",
    "submission",
    "conversationOffset",
    "attachment",
  ] as const;
  for (const field of restartFields) {
    if (evidence.restartRecovery?.[field] !== true)
      errors.push(`restartRecovery.${field} must be true`);
  }
  for (const field of [
    "identityPreserved",
    "resumedAfterRestart",
    "completedAfterApproval",
  ] as const) {
    if (evidence.approvalRecovery?.[field] !== true)
      errors.push(`approvalRecovery.${field} is missing`);
  }
  for (const field of ["sessions", "submissions", "conversationStreams", "attachments"] as const) {
    if ((evidence.cascadeDeletion?.[field] ?? 0) < 1)
      errors.push(`cascadeDeletion.${field} must be at least 1`);
  }
  if (errors.length > 0) {
    throw new Error(`Databricks App recovery evidence is invalid:\n- ${errors.join("\n- ")}`);
  }
}

/** Assert that retained App evidence proves distinct principals and cross-user denial. */
export function validateDatabricksAppUserIsolationEvidence(
  evidence: DatabricksAppUserIsolationEvidence,
): void {
  const errors: string[] = [];
  if (evidence.schemaVersion !== 1) errors.push("schemaVersion must be 1");
  if (evidence.target !== "databricks-app") errors.push("target must be databricks-app");
  if (!evidence.generatedAt || !Number.isFinite(Date.parse(evidence.generatedAt)))
    errors.push("generatedAt must be a valid timestamp");
  for (const field of [
    "principalsDistinct",
    "ownerCanList",
    "ownerCanRead",
    "otherCannotList",
  ] as const) {
    if (evidence[field] !== true) errors.push(`${field} must be true`);
  }
  if (evidence.otherDirectReadStatus !== 404) errors.push("otherDirectReadStatus must be 404");
  if (evidence.secretsAbsentFromResponses !== true)
    errors.push("secretsAbsentFromResponses must be true");
  if (evidence.principalIdentifiersRetained !== false)
    errors.push("principalIdentifiersRetained must be false");
  if (evidence.dynamicAgentIsolation !== true) errors.push("dynamicAgentIsolation must be true");
  if (evidence.dynamicOwnerCanRead !== true) errors.push("dynamicOwnerCanRead must be true");
  for (const field of [
    "dynamicOtherConversationStatus",
    "dynamicOtherSubmissionStatus",
    "dynamicOtherSessionStatus",
    "dynamicOtherAdmissionStatus",
  ] as const) {
    if (evidence[field] !== 404) errors.push(`${field} must be 404`);
  }
  if (!evidence.runIdHash?.match(/^[a-f0-9]{64}$/))
    errors.push("runIdHash must be a SHA-256 digest");
  if (!evidence.dynamicInstanceIdHash?.match(/^[a-f0-9]{64}$/))
    errors.push("dynamicInstanceIdHash must be a SHA-256 digest");
  if (errors.length > 0) {
    throw new Error(`Databricks App user-isolation evidence is invalid:\n- ${errors.join("\n- ")}`);
  }
}

/** Assert that Tier R exercised hook-authored agents across a real App restart. */
export function validateDatabricksDynamicAgentEvidence(
  evidence: DatabricksDynamicAgentEvidence,
): void {
  const errors: string[] = [];
  if (evidence.schemaVersion !== 1) errors.push("schemaVersion must be 1");
  if (evidence.target !== "databricks-app") errors.push("target must be databricks-app");
  for (const field of ["preparedAt", "verifiedAt"] as const) {
    if (!evidence[field] || !Number.isFinite(Date.parse(evidence[field]))) {
      errors.push(`${field} must be a valid timestamp`);
    }
  }
  for (const field of [
    "hookRendering",
    "conditionalModelAndTools",
    "durableToolStep",
    "crashRecoveryAfterCheckpoint",
    "exactlyOneToolStep",
    "persistentStateAfterRestart",
    "immutableInitialDataAcrossNamedSessions",
    "resourceChangeNarration",
    "dataParts",
    "responseMetadata",
    "lifecycleHooks",
    "externalDatabricksJob",
    "stableExternalIdempotency",
    "approvalRecovery",
    "rejectedApprovalPreventedExecution",
    "modelCancellation",
    "governedToolCancellation",
    "cancellationCleanup",
    "dynamicManagedMcp",
    "boundedSubagent",
    "scopedSubagentTool",
    "selfDispatchActorTenantPreserved",
    "responsesApi",
    "previousResponseId",
    "responsesStreaming",
    "cleanup",
  ] as const) {
    if (evidence[field] !== true) errors.push(`${field} must be true`);
  }
  for (const field of ["instanceIdHash", "checkpointHash"] as const) {
    if (!evidence[field]?.match(/^[a-f0-9]{64}$/)) errors.push(`${field} must be a SHA-256 digest`);
  }
  if (errors.length > 0) {
    throw new Error(`Databricks dynamic-agent evidence is invalid:\n- ${errors.join("\n- ")}`);
  }
}
